<template>
  <div class="flex flex-col gap-1 mb-3">
    <template v-if="slaWindow">
      <div class="flex items-center justify-between text-xs">
        <span class="text-slate-600 dark:text-slate-400">
          {{ $t('QUEUES_MGMT.SLA_BAR.LABEL') }}
        </span>
        <span
          :class="atRisk ? 'text-red-600 dark:text-red-400' : 'text-slate-500'"
        >
          {{ percent }}% · {{ remainingLabel }}
        </span>
      </div>

      <!-- QM §5.6.1 — consumption bar with a tick per overflow-expansion threshold -->
      <div
        class="relative h-1.5 w-full rounded bg-slate-100 dark:bg-slate-800 overflow-hidden"
      >
        <div
          class="h-full rounded"
          :class="atRisk ? 'bg-red-500' : 'bg-woot-500'"
          :style="{ width: `${percent}%` }"
        />
        <!-- QM §5.10 — logical inset so the markers mirror with the bar in RTL locales;
             a physical `left` would leave them pointing at the wrong thresholds. -->
        <span
          v-for="marker in markers"
          :key="marker.trigger"
          class="absolute top-0 h-full w-0.5 bg-slate-500 dark:bg-slate-300"
          :style="{ insetInlineStart: `${marker.at}%` }"
        />
      </div>

      <!-- Markers are also spelled out — never colour/position alone (§5.10) -->
      <p v-if="markers.length" class="text-xs text-slate-500">
        {{ $t('QUEUES_MGMT.SLA_BAR.MARKERS') }}:
        <span v-for="(marker, index) in markers" :key="marker.trigger">
          <template v-if="index">·</template>
          {{ markerLabel(marker) }}
        </span>
      </p>
    </template>

    <span v-else class="text-xs text-slate-400">
      {{ $t('QUEUES_MGMT.MY_TICKETS.NO_SLA') }}
    </span>
  </div>
</template>

<script>
import { formatDistanceToNowStrict, parseISO } from 'date-fns';

// Matches the at-risk styling MyTicketsList.vue already uses for its SLA bar.
const SLA_WARNING_PERCENT = 80;
// The bar is derived from timestamps, so it has to redraw itself between pushes.
const TICK_MS = 60000;

const toMillis = value => (value ? parseISO(value).getTime() : null);

export default {
  props: {
    routingState: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      now: Date.now(),
      timer: null,
    };
  },
  computed: {
    startedAt() {
      return toMillis(this.routingState.conversation_created_at);
    },
    deadline() {
      return toMillis(this.routingState.sla_deadline);
    },
    // Null whenever no SLA applies — the bar has no scale to draw against.
    slaWindow() {
      if (!this.startedAt || !this.deadline) return null;
      const span = this.deadline - this.startedAt;
      return span > 0 ? span : null;
    },
    percent() {
      if (!this.slaWindow) return 0;
      const consumed = ((this.now - this.startedAt) / this.slaWindow) * 100;
      return Math.round(Math.min(Math.max(consumed, 0), 100));
    },
    atRisk() {
      return this.percent >= SLA_WARNING_PERCENT;
    },
    remainingLabel() {
      if (!this.deadline) return '';
      return this.deadline <= this.now
        ? this.$t('QUEUES_MGMT.SLA_BAR.OVERDUE')
        : formatDistanceToNowStrict(new Date(this.deadline));
    },
    // QM §5.6.1 — where each enabled overflow trigger lands on the SLA scale. The time
    // trigger counts from enqueued_at, the SLA one is already a percent of the window.
    markers() {
      if (!this.slaWindow) return [];

      const {
        overflow_time_trigger_seconds: seconds,
        overflow_sla_trigger_percent: slaPercent,
        enqueued_at: enqueuedAt,
      } = this.routingState;
      const enqueued = toMillis(enqueuedAt);
      const found = [];

      if (seconds && enqueued) {
        const firesAt = enqueued + seconds * 1000;
        found.push({
          trigger: 'TIME',
          at: ((firesAt - this.startedAt) / this.slaWindow) * 100,
        });
      }
      if (slaPercent) {
        found.push({ trigger: 'SLA', at: slaPercent });
      }

      // A threshold past the deadline has no place on a 0–100 scale.
      return found
        .filter(marker => marker.at >= 0 && marker.at <= 100)
        .map(marker => ({ ...marker, at: Math.round(marker.at) }));
    },
  },
  mounted() {
    this.timer = setInterval(() => {
      this.now = Date.now();
    }, TICK_MS);
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    markerLabel(marker) {
      return `${this.$t(`QUEUES_MGMT.SLA_BAR.TRIGGER.${marker.trigger}`)} ${
        marker.at
      }%`;
    },
  },
};
</script>
